import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription, timer } from 'rxjs';

import { StopCookiesStatus, StopNotifiedUser } from '../../../models/stop-cookies.model';
import { StopCookiesService } from '../../../services/stop-cookies.service';
import { TeamService } from '../../../../services/team.service';
import { TeamMember } from '../../../../models/teamMember.model';

/**
 * Solapa "STOP": estado del proceso que renueva las cookies de SSTA
 * (agrology-stop-cookie-refresher) y disparo manual de la renovación.
 *
 * El dato es GLOBAL de la plataforma, no de la company: la sesión de SSTA es
 * una sola (login público, sin credenciales por empresa). La solapa se muestra
 * a cualquier admin y es, sobre todo, visibilidad. Lo único por company es la
 * lista de usuarios a notificar si la renovación falla.
 */
@Component({
  selector: 'ag-stop-cookies',
  templateUrl: './stop-cookies.component.html',
  styleUrls: ['./stop-cookies.component.scss']
})
export class StopCookiesComponent implements OnInit, OnDestroy {

  @Input() public companyId: number;

  public status: StopCookiesStatus;
  public loading: boolean = true;
  public error: boolean = false;

  /** Mensaje de error del backend (429 de cuota, 409 de concurrencia, etc.). */
  public actionError: string;
  /** Mensaje informativo, p. ej. "la cookie ya estaba vigente". */
  public actionMessage: string;
  public processing: boolean = false;

  // --- Usuarios a notificar ---
  public teamMembers: TeamMember[];
  public notifiedUsers: StopNotifiedUser[] = [];
  public selectedUserIds: number[] = [];
  public savingUsers: boolean = false;
  public usersSaved: boolean = false;

  /** Tope del poleo: 60 intentos de 5s = 300s. Ver comentario en pollUntilDone. */
  private readonly POLL_INTERVAL_MS = 5000;
  private readonly POLL_MAX_ATTEMPTS = 60;
  private pollAttempts: number = 0;
  private pollSubscription: Subscription;

  private subscriptions: Subscription[] = [];

  constructor(
    private stopCookiesService: StopCookiesService,
    private teamService: TeamService
  ) { }

  ngOnInit(): void {
    this.loadStatus();
    this.loadNotifiedUsers();
    this.loadTeamMembers();
  }

  /**
   * El estado "renovando" lo manda el BACKEND, no es una variable del
   * componente. Así, si el usuario se va de la pantalla y vuelve (o recarga, o
   * lo mira otro admin en paralelo), la vista reengancha sola el poleo con el
   * tiempo real transcurrido.
   */
  public get refreshing(): boolean {
    return this.status?.refreshJob?.status === 'running';
  }

  public get canRefresh(): boolean {
    return !this.processing
      && !this.refreshing
      && this.status?.rateLimit?.allowed !== false;
  }

  private loadStatus(reenganchar: boolean = true): void {
    this.subscriptions.push(
      this.stopCookiesService.getStatus(this.companyId).subscribe({
        next: status => {
          this.status = status;
          this.loading = false;

          // Si el backend dice que hay una renovación en curso (la disparó otro
          // admin, o este mismo antes de recargar), retomamos el poleo.
          if (reenganchar && this.refreshing && !this.pollSubscription) {
            this.pollUntilDone();
          }
        },
        error: () => {
          this.error = true;
          this.loading = false;
        }
      })
    );
  }

  /**
   * Dispara la renovación manual.
   *
   * El backend responde 202 y sigue trabajando por detrás: resolver el captcha
   * tarda 32-86s y el API Gateway corta a los 30s, así que no hay forma de
   * esperar la respuesta real. De ahí el poleo.
   *
   * Si la cookie ya está vigente, el backend responde 200 `already_valid` sin
   * hacer login ni consumir cuota — el objetivo es sostener el régimen de un
   * solo login por día.
   */
  public refresh(): void {
    if (!this.canRefresh) return;

    this.processing = true;
    this.actionError = null;
    this.actionMessage = null;

    this.subscriptions.push(
      this.stopCookiesService.refresh(this.companyId).subscribe({
        next: result => {
          this.processing = false;

          // El POST devuelve el DESENLACE del disparo, no el estado completo:
          // asignarlo a `this.status` dejaría la tabla de cookies vacía. Se
          // actualiza solo la cuota (para grisar el botón en el acto) y se
          // recarga el estado aparte.
          if (this.status && result?.rateLimit) this.status.rateLimit = result.rateLimit;

          if (result?.outcome === 'already_valid') {
            // Camino más frecuente por diseño: la cookie seguía vigente, no se
            // hizo login ni se gastó cuota. Se avisa para que el usuario no
            // crea que el botón no hizo nada.
            this.actionMessage = result.message;
          }

          if (result?.outcome === 'accepted') {
            // No se recarga acá: el poleo ya va a traer el estado. Un GET en
            // paralelo que tarde más de 5s devolvería datos VIEJOS (sin el job
            // 'running') y pisaría lo que el poleo ya actualizó, dejando el
            // spinner pegado.
            this.pollUntilDone();
          } else {
            this.loadStatus(false);
          }
        },
        error: response => {
          this.processing = false;
          // El backend manda un shape de error propio; si el interceptor global
          // se lo come, cae al mensaje genérico de la traducción.
          this.actionError = response?.error?.error?.message || 'STOP_COOKIES.REFRESH_ERROR';
        }
      })
    );
  }

  /**
   * Poleo cada 5s hasta que el trabajo termine, con tope de 300s.
   *
   * El tope es 300 y no 180 porque el peor caso real del refresher (3 intentos
   * de captcha) llega a ~470s: cortar antes mostraría un falso "falló" mientras
   * el trabajo sigue vivo. Por eso, al agotarse, el mensaje dice "está
   * tardando" y no "falló" — el desenlace real se ve al recargar, o llega por
   * la notificación de fallo.
   *
   * No hay reintento automático: cada disparo real gasta saldo de 2Captcha.
   */
  private pollUntilDone(): void {
    this.stopPolling();
    this.pollAttempts = 0;

    this.pollSubscription = timer(this.POLL_INTERVAL_MS, this.POLL_INTERVAL_MS).subscribe(() => {
      this.pollAttempts++;

      if (this.pollAttempts > this.POLL_MAX_ATTEMPTS) {
        this.stopPolling();
        this.actionError = 'STOP_COOKIES.REFRESH_TAKING_LONG';
        return;
      }

      // Se registra en `subscriptions` como todo lo demás: un GET en vuelo
      // cuando el usuario se va de la pantalla quedaría colgado. (Este repo
      // tiene historia de memory leaks — ver los MEMORY_LEAK_*.md de la raíz.)
      //
      // Antes de pushear se descartan las suscripciones ya cerradas: el poleo
      // corre hasta 60 veces, así que sin esto el array crecería una entrada
      // por tick y quedaría lleno de objetos muertos hasta el ngOnDestroy.
      this.subscriptions = this.subscriptions.filter(sub => !sub.closed);

      this.subscriptions.push(
        this.stopCookiesService.getStatus(this.companyId).subscribe({
          next: status => {
            this.status = status;
            if (!this.refreshing) this.stopPolling();
          },
          // Un GET fallido no corta el poleo: puede ser un blip de red mientras
          // el trabajo de fondo sigue perfectamente vivo.
          error: () => { }
        })
      );
    });
  }

  private stopPolling(): void {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
      this.pollSubscription = null;
    }
  }

  // --- Usuarios a notificar (por company) ---

  private loadNotifiedUsers(): void {
    this.subscriptions.push(
      this.stopCookiesService.getNotifiedUsers(this.companyId).subscribe({
        next: users => {
          this.notifiedUsers = users;
          this.selectedUserIds = users.map(user => user.userId);
        },
        error: () => { }
      })
    );
  }

  private loadTeamMembers(): void {
    this.subscriptions.push(
      // TeamService.get() (/team/members) y NO ManagersService.getRepresentatives()
      // (/team/representatives): ese último devuelve solo las cuentas que son
      // REPRESENTANTES de otra company, que para la mayoría de las empresas está
      // vacío. El selector habría aparecido sin una sola opción, dejando
      // inutilizable la única parte por-company de la solapa.
      this.teamService.get(this.companyId).subscribe({
        next: (members: TeamMember[]) => {
          this.teamMembers = members;
        },
        error: () => {
          this.teamMembers = [];
        }
      })
    );
  }

  /**
   * Guarda la lista completa (reemplazo), no altas y bajas sueltas: la UI es un
   * multi-select que se guarda entero.
   *
   * Se manda el objeto completo de cada usuario y no solo el id, para que el
   * backend no tenga que ir a buscar el mail a api2 justo cuando está
   * notificando una falla.
   */
  public saveNotifiedUsers(): void {
    this.savingUsers = true;
    this.usersSaved = false;
    this.actionError = null;

    // Ojo con los nombres de campo: el modelo User del repo usa snake_case
    // (`name`, `last_name`), mientras que el contrato del backend espera
    // firstName/lastName. La traducción se hace acá, una sola vez.
    const users: StopNotifiedUser[] = (this.teamMembers || [])
      .filter(member => member.user && this.selectedUserIds.includes(member.user.id))
      .map(member => ({
        userId: member.user.id,
        accountId: member.id,
        email: member.email,
        firstName: member.user.name,
        lastName: member.user.last_name,
        language: member.user.language
      } as StopNotifiedUser));

    this.subscriptions.push(
      this.stopCookiesService.saveNotifiedUsers(this.companyId, users).subscribe({
        next: saved => {
          this.notifiedUsers = saved;
          this.savingUsers = false;
          this.usersSaved = true;
        },
        error: () => {
          this.savingUsers = false;
          // Sin esto el guardado fallaba en silencio: el usuario creía que la
          // lista quedó guardada y nadie recibiría el aviso.
          this.actionError = 'STOP_COOKIES.NOTIFIED_ERROR';
        }
      })
    );
  }

  /** Refresca la vista sin disparar ninguna renovación. */
  public reload(): void {
    this.loadStatus();
  }

  /** @ignore */
  ngOnDestroy(): void {
    this.stopPolling();
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
